import React, { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { isGenericEmail } from "../context/AuthContext";
import { Eye, EyeOff, ArrowRight, ShieldCheck, BarChart3, Zap, AlertCircle, Activity } from "lucide-react";

export default function AuthPage() {
  // --- VIEW STATE ---
  const [view, setView] = useState("checking");
  const [mode, setMode] = useState("login");
  const [form, setForm] = useState({ name: "", email: "", password: "", confirm: "" });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  
  // --- NEW: Consent State ---
  const [agreedToTerms, setAgreedToTerms] = useState(false);

  const navigate = useNavigate();
  const { login, signup, isAuthenticated, currentUser } = useAuth();

  // ---------------------------------------------------------
  // CORE FLOW CONTROL LOGIC
  // ---------------------------------------------------------
  useEffect(() => {
    if (view !== "checking") return;

    const handleInitialLoad = () => {
      if (isAuthenticated && currentUser) {
        console.log("Session found. Redirecting...");
        if (currentUser.is_admin) {
          navigate("/dashboard/admin", { replace: true });
        } else {
          navigate("/dashboard", { replace: true });
        }
      } else {
        console.log("No session. Showing Intro...");
        setView("intro");

        const introTimer = setTimeout(() => {
          setView("auth");
        }, 3000);

        return () => clearTimeout(introTimer);
      }
    };

    handleInitialLoad();
  }, [view, isAuthenticated, currentUser, navigate]);

  const isLogin = mode === "login";

  const handleChange = (field) => (e) => {
    setForm((prev) => ({ ...prev, [field]: e.target.value }));
    setError("");
  };

  const toggleMode = () => {
    setMode((prev) => (prev === "login" ? "signup" : "login"));
    setError("");
    setSuccess("");
    setForm({ name: "", email: "", password: "", confirm: "" });
    setAgreedToTerms(false); // Reset consent on mode toggle
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    // NEW: Validate Terms Agreement for Signup
    if (!isLogin && !agreedToTerms) {
      setError("You must agree to the Terms and Privacy Policy to create an account.");
      return;
    }

    if (isGenericEmail(form.email)) {
      setError("Please use a real email address. Temporary or randomly generated emails are not allowed.");
      return;
    }

    setIsSubmitting(true);

    if (!isLogin && form.password !== form.confirm) {
      setError("Passwords do not match.");
      setIsSubmitting(false);
      return;
    }

    try {
      const result = isLogin
        ? await login(form.email, form.password)
        : await signup(form.name, form.email, form.password);

      if (!result?.success) {
        setError(result?.error || "Authentication failed.");
      } else {
        setSuccess("Success! Redirecting...");
      }
    } catch (err) {
      const backendError = err?.response?.data?.detail || "An unexpected error occurred.";
      setError(backendError);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    // Top-anchored at 10vh on mobile (professional auth placement, slightly
    // below center) and dead-centered on desktop. When the card is taller
    // than the phone screen it scrolls normally instead of being sliced.
    <div className="min-h-screen bg-slate-50 dark:bg-gray-900 overflow-y-auto overflow-x-hidden flex items-start md:items-center pt-[10vh] px-4 pb-4 sm:px-5 md:p-8 font-sans transition-colors duration-300">
      {/* --- 0. CHECKING SESSION STATE --- */}
      {view === "checking" && (
        <div className="fixed inset-0 z-50 bg-slate-50 dark:bg-gray-900 flex items-center justify-center">
          <div className="relative w-20 h-20 flex items-center justify-center">
            <div className="absolute inset-0 rounded-full border-4 border-dotted border-gray-300 dark:border-white/10 animate-spin"></div>
            <div className="absolute inset-2 rounded-full border-4 border-dotted border-gray-400 dark:border-white/40 animate-spin-reverse"></div>
            <div className="absolute inset-4 rounded-full border-2 border-dotted border-indigo-500 dark:border-white animate-spin" style={{ animationDuration: "5s" }}></div>
            {/* Two logo assets: light/dark variants so the transparent logo always blends with the background. */}
            <div className="w-10 h-10 rounded-full overflow-hidden z-10 flex items-center justify-center">
              <img src="/logo-light.png" alt="Loading" className="w-full h-full object-contain animate-pulse-slow dark:hidden" onError={(e) => { e.target.style.display = 'none'; }} />
              <img src="/logo.png?v=2" alt="Loading" className="hidden w-full h-full object-contain animate-pulse-slow dark:block" onError={(e) => { e.target.style.display = 'none'; }} />
            </div>
          </div>
        </div>
      )}

      {/* --- 1. SPLASH SCREEN (INTRO) --- */}
      {view === "intro" && (
        <div className="fixed inset-0 z-50 flex flex-col items-center p-6 md:p-12 bg-slate-50 dark:bg-gray-900 overflow-hidden pt-[15vh] animate-fade-in">
          <div className="w-full max-w-2xl text-left animate-fade-in-up">
            <div className="mb-8 pl-1">
              <h1 className="text-6xl md:text-7xl font-extrabold tracking-tight text-gray-900 dark:text-white mb-2">
                Edge <span className="text-transparent bg-clip-text bg-gradient-to-r from-indigo-500 to-purple-500">Hub</span>
              </h1>
              <p className="text-sm md:text-base text-indigo-500 dark:text-indigo-300 uppercase tracking-[0.2em] font-semibold">
                Intelligent Psychology & Trading
              </p>
            </div>

            <div className="mb-4 max-w-xl">
              <p className="text-lg md:text-xl text-gray-600 dark:text-gray-400 font-light leading-relaxed tracking-wide">
                A psychology app designed to help users understand emotions, track mental patterns, and improve daily decisions.
              </p>
            </div>
          </div>

          {/* Updated Loader with Circle Mask */}
          <div className="flex flex-col items-center justify-center mt-auto mb-12 animate-fade-in">
            <div className="relative w-20 h-20 mb-6 flex items-center justify-center">
              <div className="absolute inset-0 rounded-full border-4 border-dotted border-gray-300 dark:border-white/10 animate-spin"></div>
              <div className="absolute inset-2 rounded-full border-4 border-dotted border-gray-400 dark:border-white/40 animate-spin-reverse"></div>
              <div className="absolute inset-4 rounded-full border-2 border-dotted border-indigo-500 dark:border-white animate-spin" style={{ animationDuration: "5s" }}></div>

              {/* Two logo assets: light/dark variants so the transparent logo always blends with the background. */}
              <div className="w-10 h-10 rounded-full overflow-hidden z-10 flex items-center justify-center">
                <img 
                  src="/logo-light.png" 
                  alt="Loading" 
                  className="w-full h-full object-contain animate-pulse-slow dark:hidden"
                  onError={(e) => { e.target.style.display = 'none'; }}
                />
                <img 
                  src="/logo.png?v=2" 
                  alt="Loading" 
                  className="hidden w-full h-full object-contain animate-pulse-slow dark:block"
                  onError={(e) => { e.target.style.display = 'none'; }}
                />
              </div>
            </div>
            <p className="text-indigo-500 dark:text-indigo-300 font-medium tracking-[0.2em] text-sm animate-pulse">INITIALIZING</p>
          </div>
        </div>
      )}

      {/* --- 2. AUTH FORM --- */}
      {view === "auth" && (
        <div className="w-full max-w-[420px] md:max-w-2xl grid grid-cols-1 md:grid-cols-5 bg-white dark:bg-gray-900 rounded-2xl md:rounded-3xl shadow-xl dark:shadow-2xl overflow-hidden border border-transparent dark:border-gray-800/50 animate-fade-in-up">
          {/* LEFT SIDE - BRANDING (compact on mobile) */}
          <div className="col-span-1 md:col-span-2 bg-gradient-to-br from-indigo-600 via-purple-600 to-pink-500 px-5 py-4 sm:p-6 md:p-8 lg:p-10 flex flex-col justify-between relative overflow-hidden">
            <div className="absolute inset-0 bg-black/10 backdrop-blur-sm"></div>

            <div className="relative z-10">
              <h1 className="text-lg sm:text-xl md:text-2xl lg:text-3xl font-extrabold text-white tracking-tight">Edge Hub</h1>
              <p className="text-indigo-100 mt-0.5 sm:mt-1 text-[10px] md:text-xs opacity-80">Intelligent Trading Platform</p>
            </div>

            <div className="relative z-10 space-y-4 mt-8 hidden sm:block">
              <FeatureItem icon={Activity} text="Psychology-Based Insights" />
              <FeatureItem icon={BarChart3} text="Advanced Pattern Analytics" />
              <FeatureItem icon={Zap} text="Real-time MT5 Integration" />
              <FeatureItem icon={ShieldCheck} text="Secure & Automated" />
            </div>

            <div className="relative z-10 mt-4 sm:mt-8">
              <p className="text-[10px] text-indigo-100 opacity-60">© 2025 AI Trade Hub. All rights reserved.</p>
            </div>
          </div>

          {/* RIGHT SIDE - FORM */}
          <div className="col-span-1 md:col-span-3 p-5 sm:p-7 md:p-10 lg:p-12 relative bg-white dark:bg-gray-900">
            <div className="w-full mb-5 sm:mb-6">
              <div className="flex bg-gray-100 dark:bg-gray-800/60 rounded-full p-1 border border-gray-200 dark:border-gray-700/50">
                <button
                  type="button"
                  onClick={() => !isLogin && toggleMode()}
                  className={`flex-1 px-4 py-2.5 rounded-full text-sm font-medium transition-all duration-300 ${
                    isLogin ? "bg-indigo-600 text-white shadow-lg" : "text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white"
                  }`}
                >
                  Login
                </button>
                <button
                  type="button"
                  onClick={() => isLogin && toggleMode()}
                  className={`flex-1 px-4 py-2.5 rounded-full text-sm font-medium transition-all duration-300 ${
                    !isLogin ? "bg-indigo-600 text-white shadow-lg" : "text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white"
                  }`}
                >
                  Sign Up
                </button>
              </div>
            </div>

            <div className="mb-5 sm:mb-6 text-center md:text-left">
              <h2 className="text-lg md:text-xl lg:text-2xl font-bold text-gray-900 dark:text-white">{isLogin ? "Welcome back" : "Create account"}</h2>
              <p className="text-gray-500 dark:text-gray-400 text-xs md:text-sm mt-1">
                {isLogin ? "Enter your credentials to access your account" : "Start your trading journey today"}
              </p>
            </div>

            {error && (
              <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-500/30 rounded-lg text-red-600 dark:text-red-300 text-xs flex items-center gap-2">
                <AlertCircle size={14} />
                {error}
              </div>
            )}
            {success && (
              <div className="mb-4 p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-500/30 rounded-lg text-green-600 dark:text-green-300 text-xs">
                {success}
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
              {!isLogin && <InputField label="Username" value={form.name} onChange={handleChange("name")} placeholder="John Doe" />}

              <InputField label="Email Address" type="email" value={form.email} onChange={handleChange("email")} placeholder="you@example.com" />

              <div className="space-y-1 relative">
                <label className="block text-[11px] font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">Password</label>
                <div className="relative">
                  <input
                    type={showPassword ? "text" : "password"}
                    value={form.password}
                    onChange={handleChange("password")}
                    placeholder="••••••••"
                    required
                    className="w-full px-3 py-2.5 pr-10 bg-gray-100 dark:bg-gray-800/60 border border-gray-200 dark:border-gray-700/50 rounded-lg text-gray-900 dark:text-white text-sm placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-transparent transition-all"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors p-1 z-10"
                  >
                    {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
              </div>

              {!isLogin && <InputField label="Confirm Password" type="password" value={form.confirm} onChange={handleChange("confirm")} placeholder="••••••••" />}

              {/* --- NEW: TERMS & CONDITIONS CHECKBOX --- */}
              {!isLogin && (
                <div className="flex items-start gap-2 pt-2">
                  <div className="flex items-center h-5">
                    <input
                      id="terms"
                      name="terms"
                      type="checkbox"
                      checked={agreedToTerms}
                      onChange={() => setAgreedToTerms(!agreedToTerms)}
                      className="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300 dark:border-gray-600 rounded bg-gray-100 dark:bg-gray-800"
                    />
                  </div>
                  <label htmlFor="terms" className="ml-2 text-xs text-gray-600 dark:text-gray-400">
                    I agree to the{' '}
                    <Link to="/terms" className="font-medium text-indigo-600 hover:text-indigo-500 dark:text-indigo-400">
                      Terms
                    </Link>
                    {' '}and{' '}
                    <Link to="/privacy" className="font-medium text-indigo-600 hover:text-indigo-500 dark:text-indigo-400">
                      Privacy Policy
                    </Link>.
                  </label>
                </div>
              )}

              <button
                type="submit"
                disabled={isSubmitting || (!isLogin && !agreedToTerms)}
                className="w-full py-3 rounded-xl font-semibold bg-indigo-600 text-white hover:bg-indigo-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 shadow-md mt-2"
              >
                {isSubmitting ? (
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                ) : (
                  <>
                    {isLogin ? "Sign In" : "Create Account"}
                    <ArrowRight size={16} />
                  </>
                )}
              </button>

              {isLogin && (
                <div className="text-right mt-2">
                  <Link to="/forgot-password" className="text-xs text-indigo-500 dark:text-indigo-400 hover:text-indigo-600 dark:hover:text-indigo-300 transition-colors">
                    Forgot Password?
                  </Link>
                </div>
              )}
            </form>

            <div className="mt-6 sm:mt-8">
              <div className="relative flex py-2 items-center">
                <div className="flex-grow border-t border-gray-200 dark:border-gray-800"></div>
                <span className="flex-shrink mx-4 text-xs text-gray-400 dark:text-gray-500 uppercase tracking-wider">Or continue with</span>
                <div className="flex-grow border-t border-gray-200 dark:border-gray-800"></div>
              </div>

              <div className="flex justify-center gap-4 mt-4 sm:mt-5">
                <SocialButton href="https://www.tiktok.com/@edgehubfx?_r=1&_t=ZS-94QsIZz8kB0" icon="tiktok" />
                <SocialButton href="https://x.com/edgehubx?s=21" icon="twitter" />
                <SocialButton href="https://www.instagram.com/edgehubfx?igsh=NzF4anB2ZGh1Nnlr&utm_source=qr" icon="instagram" />
              </div>
            </div>
          </div>
        </div>
      )}

      <style jsx>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes fadeInUp {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes pulseSlow {
          0%, 100% { transform: scale(1); opacity: 1; }
          50% { transform: scale(1.1); opacity: 0.8; }
        }
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        @keyframes spinReverse {
          0% { transform: rotate(360deg); }
          100% { transform: rotate(0deg); }
        }
        .animate-fade-in { animation: fadeIn 0.5s ease-out forwards; }
        .animate-fade-in-up { animation: fadeInUp 0.8s ease-out forwards; }
        .animate-pulse-slow { animation: pulseSlow 2s ease-in-out infinite; }
        .animate-spin { animation: spin 1s linear infinite; }
        .animate-spin-reverse { animation: spinReverse 1.5s linear infinite; }
      `}</style>
    </div>
  );
}

function InputField({ label, type = "text", value, onChange, placeholder }) {
  return (
    <div className="space-y-1">
      <label className="block text-[11px] font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">{label}</label>
      <input
        type={type}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        required
        className="w-full px-3 py-2.5 bg-gray-100 dark:bg-gray-800/60 border border-gray-200 dark:border-gray-700/50 rounded-lg text-gray-900 dark:text-white text-sm placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-transparent transition-all"
      />
    </div>
  );
}

function FeatureItem({ icon: Icon, text }) {
  return (
    <div className="flex items-center gap-3 text-white">
      <div className="p-1.5 bg-white/10 rounded-md backdrop-blur-sm">
        <Icon size={14} />
      </div>
      <span className="font-medium text-xs">{text}</span>
    </div>
  );
}

function SocialButton({ href, icon }) {
  const icons = {
    tiktok: (
      <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
        <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z" />
      </svg>
    ),
    twitter: (
      <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
        <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
      </svg>
    ),
    instagram: (
      <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <rect x="2" y="2" width="20" height="20" rx="5" ry="5"></rect>
        <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z"></path>
        <line x1="17.5" y1="6.5" x2="17.51" y2="6.5"></line>
      </svg>
    ),
  };

  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="w-11 h-11 sm:w-12 sm:h-12 rounded-full bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 border border-gray-200 dark:border-gray-700 flex items-center justify-center text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-all shadow-sm hover:shadow-md"
    >
      {icons[icon]}
    </a>
  );
}